/**
 * Structured Logger - Centralized logging with level-based filtering
 * Replaces scattered console.log/warn/error calls throughout the codebase
 * for consistent production debugging and development visibility.
 * 
 * Usage:
 *   logger.debug('User action', { clipId: '001' });  // Only in dev mode
 *   logger.info('Clips loaded', { count: 42 });       // Always shown
 *   logger.warn('Missing field', { field: 'difficulty' });
 *   logger.error('Failed to load', { error: err });
 * 
 * Debug mode is enabled by:
 *   - localStorage.listenUpDev = '1' (checked at logger initialization)
 *   - window.__listenUpLogger.setDebug(true) (runtime toggle)
 */

type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

interface Logger {
  debug(msg: string, data?: unknown): void;
  info(msg: string, data?: unknown): void;
  warn(msg: string, data?: unknown): void;
  error(msg: string, data?: unknown): void;
  setDebug(enabled: boolean): boolean;
  isDebugEnabled(): boolean;
}

const logger: Logger = (function(): Logger {
  // Check if debug mode is enabled
  let isDev = false;
  try {
    isDev = localStorage.getItem('listenUpDev') === '1';
  } catch (e) {
    // localStorage might not be available (private browsing, etc.)
    isDev = false;
  }
  
  // Format a log message with prefix and optional data
  function formatMessage(level: LogLevel, msg: string, data?: unknown): [string, string] | [string, string, unknown] {
    const prefix = `[${level}]`;
    if (data !== undefined) {
      return [prefix, msg, data];
    }
    return [prefix, msg];
  }
  
  // Public API
  return {
    // Debug-level logs (only in dev mode)
    debug: (msg: string, data?: unknown): void => {
      if (isDev) {
        console.log(...formatMessage('DEBUG', msg, data));
      }
    },
    
    // Informational logs (always shown)
    info: (msg: string, data?: unknown): void => {
      console.log(...formatMessage('INFO', msg, data));
    },
    
    // Warning logs (always shown)
    warn: (msg: string, data?: unknown): void => {
      console.warn(...formatMessage('WARN', msg, data));
    },
    
    // Error logs (always shown)
    error: (msg: string, data?: unknown): void => {
      console.error(...formatMessage('ERROR', msg, data));
    },
    
    // Runtime toggle for debug mode
    setDebug: (enabled: boolean): boolean => {
      isDev = !!enabled;
      console.log(`[LOGGER] Debug mode ${isDev ? 'enabled' : 'disabled'}`);
      return isDev;
    },
    
    // Check if debug mode is active
    isDebugEnabled: (): boolean => isDev
  };
})();

export default logger;

// Expose logger to window for developer debugging
declare global {
  interface Window {
    __listenUpLogger?: {
      setDebug: (enabled: boolean) => boolean;
      isDebugEnabled: () => boolean;
    };
  }
}

if (typeof window !== 'undefined') {
  window.__listenUpLogger = {
    setDebug: logger.setDebug,
    isDebugEnabled: logger.isDebugEnabled
  };
}
